import '@angular/localize';

export const IMPORT_DATA_CONFIG = {
  texts: {
    pageTitle: $localize`:@@importData.pageTitle:Import Data Models`,
    pageSubtitle: $localize`:@@importData.pageSubtitle:Upload your spreadsheets or JSON files to automatically generate and ingest data models using AI.`,

    // Disclaimer
    disclaimerTitle: $localize`:@@importData.disclaimerTitle:AI Analysis & Privacy Notice`,
    disclaimerText: $localize`:@@importData.disclaimerText:AI-generated recommendations are based on your file content and may not always be accurate. Please carefully review and edit any recommendations before importing your data. By uploading a file, you consent to a small sample from the beginning of the file being processed by AI for recommendation purposes. Your data remains secure, is not shared with third parties, and is not used to train or improve our AI models.`,

    uploadTitle: $localize`:@@importData.uploadTitle:Click or drag files to this area to upload`,
    uploadSubtitle: $localize`:@@importData.uploadSubtitle:Support for a single or bulk upload. Strictly prohibited from uploading company data or other banned files.`,
    supportedFiles: $localize`:@@importData.supportedFiles:Supported formats: .csv, .tsv, .json, .jsonl, .xlsx, .xls, .ods`,
    analyzeButton: $localize`:@@importData.analyzeButton:Analyze Files with AI`,
    reanalyzeButton: $localize`:@@importData.reanalyzeButton:Re-analyze`,
    importButton: $localize`:@@importData.importButton:Start Import`,
    cancelButton: $localize`:@@importData.cancelButton:Cancel`,
    backButton: $localize`:@@importData.backButton:Back to Overview`,

    importingFilesTitle: $localize`:@@importData.importingFilesTitle:Importing Files`,
    expandAllButton: $localize`:@@importData.expandAllButton:Expand All`,
    collapseAllButton: $localize`:@@importData.collapseAllButton:Collapse All`,

    // Schema Editor Texts
    aiRecommendationsTitle: $localize`:@@importData.aiRecommendationsTitle:AI Recommendations`,
    customizeRecommendationsTitle: $localize`:@@importData.customizeRecommendationsTitle:Customize Recommendations`,
    collapsedMessage: $localize`:@@importData.collapsedMessage:This file is collapsed, click here or the expand button to see the details and recommendations for this file.`,
    modelNameLabel: $localize`:@@importData.modelNameLabel:Model Name`,
    modelDescLabel: $localize`:@@importData.modelDescLabel:Model Description`,
    colOriginal: $localize`:@@importData.colOriginal:Original Column`,
    colField: $localize`:@@importData.colField:Field Name`,
    colTitle: $localize`:@@importData.colTitle:UI Title`,
    colType: $localize`:@@importData.colType:Data Type`,
    colDesc: $localize`:@@importData.colDesc:Description`,
    colImport: $localize`:@@importData.colImport:Import?`,
    colRequired: $localize`:@@importData.colRequired:Required?`,
    colSensitive: $localize`:@@importData.colSensitive:Sensitive?`,
    invalidFieldName: $localize`:@@importData.invalidFieldName:Field name must be in camelCase or snake_case.`,

    // Modals
    confirmRemoveTitle: $localize`:@@importData.confirmRemoveTitle:Remove File`,
    confirmRemoveContent: $localize`:@@importData.confirmRemoveContent:Are you sure you want to remove this file and discard its AI recommendations?`,
    confirmReanalyzeTitle: $localize`:@@importData.confirmReanalyzeTitle:Re-analyze File`,
    confirmReanalyzeContent: $localize`:@@importData.confirmReanalyzeContent:AI will re-analyze the file and override any manual changes you have made. Do you want to continue?`,

    // Success Summary
    successTitle: $localize`:@@importData.successTitle:Import Successful!`,
    successSubtitle: $localize`:@@importData.successSubtitle:Your data models have been created and the data has been ingested successfully.`,
    summaryModelName: $localize`:@@importData.summaryModelName:Created Model`,
    summaryFieldsCount: $localize`:@@importData.summaryFieldsCount:Imported Fields`,
    continueButton: $localize`:@@importData.continueButton:Go to Dashboard`,
  },
  notifications: {
    fileAdded: $localize`:@@importData.notify.fileAdded:File added. Analysis started...`,
    fileRemoved: $localize`:@@importData.notify.fileRemoved:File removed successfully.`,
    analyzeError: $localize`:@@importData.notify.analyzeError:Failed to analyze file. Please check format.`,
    importSuccess: $localize`:@@importData.notify.importSuccess:Files imported successfully.`,
    importError: $localize`:@@importData.notify.importError:An error occurred during ingestion.`,
  },
  dataTypes: [
    { label: 'string', value: 'string' },
    { label: 'string array', value: 'string_array' },
    { label: 'number', value: 'number' },
    { label: 'number array', value: 'number_array' },
    { label: 'boolean', value: 'boolean' },
    { label: 'boolean array', value: 'boolean_array' },
    { label: 'date', value: 'date' },
    { label: 'date array', value: 'date_array' },
    { label: 'object', value: 'object' },
    { label: 'object array', value: 'object_array' },
    { label: 'any', value: 'any' },
    { label: 'any array', value: 'any_array' },
  ],
};

export interface AiSchemaField {
  originalColumnName: string;
  suggestedFieldName: string;
  suggestedTitle: string;
  type: string;
  isArray?: boolean;
  description: string;
  skip: boolean;
  required?: boolean;
  sensitive?: boolean;
  metadata?: { key: string; value: string }[];
}

export interface AiSchema {
  modelName: string;
  modelDescription: string;
  fields: AiSchemaField[];
}

export interface FileAnalysisState {
  id: string; // Unique identifier for Angular track stability
  file: File;
  schema?: AiSchema;
  originalSchema?: AiSchema;
  isAnalyzing: boolean;
  isExpanded: boolean;
  error?: string;
}

/**
 * PURE FUNCTION: Validates if a field name string is strictly camelCase or snake_case.
 * @param {string} name - The field name string to validate.
 * @returns {boolean} True if the format is valid, false otherwise.
 */
export function isValidFieldName(name: string): boolean {
  if (!name) return false;
  const camelCaseRegex = /^[a-z][a-zA-Z0-9]*$/;
  const snakeCaseRegex = /^[a-z][_a-z0-9]*$/;
  return camelCaseRegex.test(name) || snakeCaseRegex.test(name);
}

/**
 * PURE FUNCTION: Calculates a unique combined dropdown value based on type and array flag.
 * @param {string} type - The base data type.
 * @param {boolean} isArray - Whether the type is an array.
 * @returns {string} The combined select value (e.g., 'string_array').
 */
export function getCombinedDataType(
  type: string,
  isArray: boolean = false,
): string {
  if (!type) return 'string';
  return isArray ? `${type.toLowerCase()}_array` : type.toLowerCase();
}

/**
 * PURE FUNCTION: Parses the combined dropdown selection into explicit type and array flags.
 * @param {string} combinedType - The value emitted by the dropdown selection.
 * @returns {{ type: string, isArray: boolean }} Structured output mapping exactly to schema requirements.
 */
export function parseCombinedDataType(combinedType: string): {
  type: string;
  isArray: boolean;
} {
  if (!combinedType) return { type: 'string', isArray: false };
  if (combinedType.endsWith('_array')) {
    return { type: combinedType.replace('_array', ''), isArray: true };
  }
  return { type: combinedType, isArray: false };
}

/**
 * PURE FUNCTION: Builds the multipart/form-data payload required for the AI schema recommendation endpoint.
 * @param {File} file - The physical file to be analyzed.
 * @returns {FormData} The constructed form data object.
 */
export function buildAiSchemaFormData(file: File): FormData {
  const formData = new FormData();
  formData.append('file', file);
  return formData;
}

/**
 * PURE FUNCTION: Enriches an AI schema by injecting original column names as metadata into each field.
 * This guarantees that even if a user edits field names, the system retains a definitive mapping.
 * @param {AiSchema} schema - The original schema.
 * @returns {AiSchema} A new schema object with enriched fields.
 */
export function enrichSchemaWithMetadata(schema: AiSchema): AiSchema {
  const enrichedFields = schema.fields.map((field) => {
    const metadataWithoutOriginalColumnName = (field.metadata || []).filter(
      (metadataEntry) => metadataEntry.key !== 'originalColumnName',
    );

    return {
      ...field,
      metadata: [
        ...metadataWithoutOriginalColumnName,
        { key: 'originalColumnName', value: field.originalColumnName },
      ],
    };
  });
  return { ...schema, fields: enrichedFields };
}

/**
 * PURE FUNCTION: Builds the payload for the Ingest V3 endpoint.
 * Includes the invocation of `enrichSchemaWithMetadata` to automatically append the metadata array on export.
 * @param {FileAnalysisState[]} analysisStates - The array of files and their modified schemas.
 * @param {string} dataSourceName - The name to assign to the new data source group.
 * @returns {any} The constructed payload object matching the SDK parameter requirements.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function buildIngestV3Payload(
  analysisStates: FileAnalysisState[],
  dataSourceName: string,
): any {
  const fileMetadata: any[] = [];
  const files: File[] = [];

  analysisStates.forEach((state) => {
    if (state.schema && !state.error) {
      files.push(state.file);

      const schemaWithMetadata = enrichSchemaWithMetadata(state.schema);

      fileMetadata.push({
        originalFilename: state.file.name,
        metadata: [
          {
            key: 'aiSchemaConfig',
            value: schemaWithMetadata,
          },
        ],
      });
    }
  });

  const params = {
    body: {
      body: {
        name: dataSourceName ?? `ImportFiles_${Date.now()}`,
        connector: 'file-storage',
        title: dataSourceName ?? `Import Files ${Date.now()}`,
        isApproved: true,
        dataSource: {
          name: dataSourceName ?? `ImportFiles_${Date.now()}`,
          connector: 'file-storage',
        },
      },
      files: files,
      // Note that if a property is an array; the SDK will append it as multiple entries rather than a single JSON string, so we stringify the inner config but not the entire array
      fileMetadata: JSON.stringify(fileMetadata),
    },
  };

  return params;
}

/**
 * PURE FUNCTION: Performs a deep clone of the AI Schema to store an unmodified original copy.
 * @param {AiSchema} schema - The schema to clone.
 * @returns {AiSchema} A deeply cloned schema object.
 */
export function cloneAiSchema(schema: AiSchema): AiSchema {
  return JSON.parse(JSON.stringify(schema));
}

/**
 * PURE FUNCTION: Toggles the boolean isExpanded property of a FileAnalysisState immutably.
 * @param {FileAnalysisState} state - The current state object.
 * @returns {FileAnalysisState} A new state object with toggled expansion.
 */
export function toggleFileExpansionState(
  state: FileAnalysisState,
): FileAnalysisState {
  return { ...state, isExpanded: !state.isExpanded };
}

/**
 * PURE FUNCTION: Updates the expansion state of all file analysis states.
 * @param {FileAnalysisState[]} states - The array of current states.
 * @param {boolean} isExpanded - The target expansion state.
 * @returns {FileAnalysisState[]} A new array of states with updated expansion flags.
 */
export function setAllFilesExpansionState(
  states: FileAnalysisState[],
  isExpanded: boolean,
): FileAnalysisState[] {
  return states.map((state) => ({ ...state, isExpanded }));
}

/**
 * PURE FUNCTION: Updates a specific field property within an AI Schema immutably.
 * @param {AiSchema} schema - The original schema.
 * @param {number} fieldIndex - The index of the field to update.
 * @param {Partial<AiSchemaField>} updates - The properties to update on the field.
 * @returns {AiSchema} A new schema object with the updated field.
 */
export function updateSchemaField(
  schema: AiSchema,
  fieldIndex: number,
  updates: Partial<AiSchemaField>,
): AiSchema {
  const newFields = [...schema.fields];
  newFields[fieldIndex] = { ...newFields[fieldIndex], ...updates };
  return { ...schema, fields: newFields };
}

/**
 * PURE FUNCTION: Updates root properties of an AI Schema immutably.
 * @param {AiSchema} schema - The original schema.
 * @param {Partial<AiSchema>} updates - The root properties to update.
 * @returns {AiSchema} A new schema object.
 */
export function updateSchemaRoot(
  schema: AiSchema,
  updates: Partial<AiSchema>,
): AiSchema {
  return { ...schema, ...updates };
}
